<template>
  <div class="p-4 sm:p-6 lg:p-8">
    <div class="max-w-5xl">
      <div class="mb-6">
        <h1 class="text-2xl font-semibold text-base-content">Deployment health</h1>
        <p class="mt-2 text-sm text-base-content/70">
          Runs a live verification of the database, default organization, vector database, temporary agent, and LLM connection.
        </p>
      </div>

      <div v-if="isCheckingAvailability === true" class="card bg-base-200 shadow-sm">
        <div class="card-body items-center py-10">
          <span class="loading loading-spinner loading-lg"></span>
          <span class="text-sm text-base-content/70">Checking availability...</span>
        </div>
      </div>

      <div v-else-if="isEnabled === false" class="card bg-base-200 shadow-sm">
        <div class="card-body">
          <h2 class="card-title">Deployment health checks are disabled</h2>
          <p class="text-sm text-base-content/70">Set <code>enableDeplyomentHealth</code> to a value other than <code>false</code> in the backend environment to enable this page.</p>
        </div>
      </div>

      <div v-else>
        <div class="card bg-base-200 shadow-sm">
          <div class="card-body">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 class="card-title">Run live deployment verification</h2>
                <p class="mt-1 text-sm text-base-content/70">The temporary agent is removed after the check completes.</p>
              </div>
              <button class="btn mt-4 sm:mt-0" :disabled="isRunning === true" @click="runHealthCheck">
                <span v-if="isRunning === true" class="loading loading-spinner loading-sm"></span>
                {{ isRunning === true ? 'Running checks...' : 'Run checks' }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="requestError !== ''" class="card mt-6 border border-error bg-error/10 shadow-sm">
          <div class="card-body">
            <h2 class="card-title text-error">Could not run deployment health checks</h2>
            <p class="text-sm">{{ requestError }}</p>
          </div>
        </div>

        <div v-if="result !== null" class="mt-6">
          <div :class="result.status === 'healthy' ? 'alert alert-success' : 'alert alert-error'">
            <span>{{ result.status === 'healthy' ? 'All deployment checks passed.' : 'One or more deployment checks failed.' }}</span>
          </div>
          <p class="mt-3 text-sm text-base-content/70">Checked {{ formatTimestamp(result.checkedAt) }}</p>

          <div class="mt-4 grid grid-cols-1 md:grid-cols-2">
            <article v-for="check in result.checks" :key="check.name" class="card border border-base-300 bg-base-100 shadow-sm">
              <div class="card-body">
                <div class="flex items-start justify-between">
                  <h2 class="card-title text-base">{{ check.name }}</h2>
                  <span :class="getStatusClass(check.status)">{{ getStatusLabel(check.status) }}</span>
                </div>
                <p class="text-sm text-base-content/70">{{ check.message }}</p>
                <p class="text-xs text-base-content/50">{{ check.durationMs }} ms</p>
              </div>
            </article>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import axios from '../api/axiosInstance';
import { globalState } from '@/globalState';

const isCheckingAvailability = ref(true);
const isEnabled = ref(false);
const isRunning = ref(false);
const requestError = ref('');
const result = ref(null);

function getErrorMessage(error) {
  if (typeof error?.response?.data?.error === 'string' && error.response.data.error.trim() !== '') {
    return error.response.data.error;
  }
  if (typeof error?.message === 'string' && error.message.trim() !== '') {
    return error.message;
  }
  return 'The deployment health check could not be completed.';
}

function getStatusClass(status) {
  if (status === 'passed') {
    return 'badge badge-success';
  }
  if (status === 'failed') {
    return 'badge badge-error';
  }
  return 'badge badge-ghost';
}

function getStatusLabel(status) {
  if (status === 'passed') {
    return 'Passed';
  }
  if (status === 'failed') {
    return 'Failed';
  }
  return 'Pending';
}

function formatTimestamp(value) {
  if (typeof value !== 'string' || value === '') {
    return 'just now';
  }
  return new Intl.DateTimeFormat(navigator.language, {
    dateStyle: 'medium',
    timeStyle: 'medium'
  }).format(new Date(value));
}

async function loadAvailability() {
  isCheckingAvailability.value = true;
  requestError.value = '';
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/health/deployment/availability`);
    isEnabled.value = response.data.enabled === true;
  } catch (error) {
    requestError.value = getErrorMessage(error);
  }
  isCheckingAvailability.value = false;
}

async function runHealthCheck() {
  isRunning.value = true;
  requestError.value = '';
  result.value = null;
  try {
    const response = await axios.post(`${globalState.apiEndpoint}/api/health/deployment/run`);
    result.value = response.data;
  } catch (error) {
    if (error?.response?.data?.checks !== undefined && error.response.data.checks !== null) {
      result.value = error.response.data;
    } else {
      requestError.value = getErrorMessage(error);
    }
  }
  isRunning.value = false;
}

onMounted(() => {
  loadAvailability();
});
</script>
